import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import CardList from '../vlist/CardList/CardList';
import AutoSizer from 'react-virtualized/dist/es/AutoSizer';
import { treeDFS } from '../../../util';
import VListTreeCard from './VListTreeCard';
import useThrottleForEvent from '../../../hooks/useThrottleForEvent';
import useDebounceForEvent from '../../../hooks/useDebounceForEvent';
import useStateRef from './useStateRef';
import useCreateCallbackForEvent from '@web-mail/hooks/useCreateCallbackForEvent';
const TreeCardHeight = 36;
const TreeCardMargin = 0;
const getListIndexByStBottom = (st, listHeight = 0) => {
    let index = 0;
    try {
        index = Math.ceil((st + listHeight) / (TreeCardHeight + TreeCardMargin));
    }
    catch (e) {
        console.error('[Error:listHasMore]', e);
    }
    return index;
};
const defaultGetCardHeight = () => {
    return TreeCardHeight;
};
const defaultGetUniqueKey = (item) => item.key + '';
const VListTree = props => {
    const { onLoadMoreNode, width, height, treeData, icon, expandedKeys, onExpand, hasMore = false, scrollTop, rootClassName, rootStyle, selectedKeys, onRightClick, onSelect, selectAndExpand = false, titleRender, loadData, onScroll, cardGroupDecorate, realSize = false, getCardHeight = defaultGetCardHeight, cardClassName, getUniqKey = defaultGetUniqueKey, } = props;
    const getUniqKeyRef = useCreateCallbackForEvent(getUniqKey);
    const treeDataSubState = useMemo(() => {
        const key2NodeMap = {};
        const idList = [];
        const key2ConfigMap = {};
        if (treeData && treeData.length) {
            treeDFS(treeData, (node, config) => {
                var _a;
                if (node) {
                    let parent = null;
                    if ((config === null || config === void 0 ? void 0 : config.path) && ((_a = config === null || config === void 0 ? void 0 : config.path) === null || _a === void 0 ? void 0 : _a.length) > 1) {
                        parent = config === null || config === void 0 ? void 0 : config.path[(config === null || config === void 0 ? void 0 : config.path.length) - 2];
                    }
                    key2NodeMap[getUniqKeyRef(node)] = node;
                    key2ConfigMap[getUniqKeyRef(node)] = {
                        deep: config === null || config === void 0 ? void 0 : config.deep,
                        parent,
                    };
                }
            });
            treeData === null || treeData === void 0 ? void 0 : treeData.forEach(item => {
                idList.push(getUniqKeyRef(item));
            });
        }
        return {
            rootIdList: idList,
            key2NodeMap: key2NodeMap,
            key2ConfigMap: key2ConfigMap,
        };
    }, [treeData]);
    const { key2NodeMap } = treeDataSubState;
    const { rootIdList } = treeDataSubState;
    const { key2ConfigMap } = treeDataSubState;
    const [_scrollTop, _setScrollTop] = useState(0);
    const _stRef = useStateRef(_scrollTop);
    const [_expandedKeys, _setExpandedKeys] = useState([]);
    const [nodeLoadingStateMap, setNodeLoadingStateMap] = useState({});
    const [listIsLoading, setListIsLoading] = useState(false);
    const listIsLoadingRef = useRef(listIsLoading);
    const [splitKey, setSplitKey] = useState(null);
    const autoSize = useMemo(() => {
        return width == null && height == null;
    }, [width, height]);
    const asyncLoad = useMemo(() => {
        return !!loadData;
    }, [loadData]);
    const _nodeKeyExpandMap = useMemo(() => {
        const map = {};
        if (_expandedKeys) {
            if (_expandedKeys && _expandedKeys.length) {
                _expandedKeys.forEach(item => {
                    if (item != null) {
                        map[item] = true;
                    }
                });
            }
        }
        return map;
    }, [_expandedKeys]);
    const selectedKeysKey = useMemo(() => {
        return selectedKeys === null || selectedKeys === void 0 ? void 0 : selectedKeys.join(',');
    }, [selectedKeys]);
    const selectKeyMap = useMemo(() => {
        const map = {};
        if (selectedKeys && selectedKeys.length) {
            selectedKeys.forEach((item) => {
                map[item] = true;
            });
        }
        return map;
    }, [selectedKeysKey]);
    const nodeKeyExpandMap = useMemo(() => {
        const map = {};
        if (expandedKeys) {
            expandedKeys.forEach(item => {
                if (item != null) {
                    map[item] = true;
                }
            });
        }
        return map;
    }, [expandedKeys]);
    const curNodeKeyExpandMap = useMemo(() => {
        return expandedKeys ? nodeKeyExpandMap : _nodeKeyExpandMap;
    }, [expandedKeys, nodeKeyExpandMap, _nodeKeyExpandMap]);
    const flatNodeList = useCallback((key) => {
        var _a;
        if (key && key2NodeMap[key]) {
            const node = key2NodeMap[key];
            if (node && (node === null || node === void 0 ? void 0 : node.children) && ((_a = node === null || node === void 0 ? void 0 : node.children) === null || _a === void 0 ? void 0 : _a.length)) {
                const keyList = [];
                const children = node === null || node === void 0 ? void 0 : node.children;
                for (let i = 0; i < children.length; i++) {
                    const nodeKey = getUniqKeyRef(children[i]);
                    keyList.push(nodeKey);
                    if (curNodeKeyExpandMap[nodeKey]) {
                        keyList.push(...flatNodeList(nodeKey));
                    }
                }
                return keyList;
            }
        }
        return [];
    }, [key2NodeMap, curNodeKeyExpandMap]);
    const [showKeyList, showKey2Index] = useMemo(() => {
        const list = [];
        const key2IndexMap = {};
        for (let i = 0; i < rootIdList.length; i++) {
            const nodeKey = rootIdList[i];
            list.push(nodeKey);
            if (curNodeKeyExpandMap[nodeKey]) {
                list.push(...flatNodeList(nodeKey));
            }
        }
        if (list && list.length) {
            list.forEach((key, index) => (key2IndexMap[key] = index));
        }
        return [list, key2IndexMap];
    }, [rootIdList, flatNodeList, curNodeKeyExpandMap]);
    const resKeyList = useMemo(() => {
        if (splitKey != null) {
            const splitIndex = showKey2Index[splitKey];
            if (splitIndex != null) {
                return showKeyList.slice(0, splitIndex + 1);
            }
        }
        return showKeyList;
    }, [showKeyList, splitKey, showKey2Index]);
    const mgScrollTop = useMemo(() => {
        if (scrollTop != null) {
            return scrollTop;
        }
        return _scrollTop;
    }, [_scrollTop, scrollTop]);
    const handleCardExpand = useCallback((key, data, expanded) => {
        const logicExpandKeys = expandedKeys || _expandedKeys;
        let resKey = [];
        if (expanded) {
            resKey = [...(logicExpandKeys ? logicExpandKeys : []), key];
        }
        else {
            resKey = [...(logicExpandKeys ? logicExpandKeys.filter(item => item != key) : [])];
        }
        onExpand &&
            onExpand(resKey, {
                expanded,
                node: data,
            });
        if (!expandedKeys) {
            _setExpandedKeys(resKey);
        }
    }, [expandedKeys, _expandedKeys, onExpand]);
    const handleCardRightClick = useCallback((event, data) => {
        onRightClick &&
            onRightClick({
                node: data,
                event,
            });
    }, [onRightClick]);
    const handleCardClick = useCallback((key, event, data) => {
        if (onSelect) {
            onSelect([key], {
                selected: true,
                node: data,
                event,
            });
        }
        if (selectAndExpand && !data.isLeaf) {
            handleExpand(!(expandedKeys === null || expandedKeys === void 0 ? void 0 : expandedKeys.includes(key)), data);
        }
    }, [onSelect, selectAndExpand, expandedKeys]);
    const handleCardLoadChildren = useCallback((node) => {
        const key = getUniqKeyRef(node);
        setNodeLoadingStateMap(map => {
            return Object.assign(Object.assign({}, map), { [key + '']: true });
        });
        if (loadData) {
            const p = loadData(node);
            if (p === null || p === void 0 ? void 0 : p.then) {
                p.then(res => {
                    setNodeLoadingStateMap(map => {
                        return Object.assign(Object.assign({}, map), { [key + '']: false });
                    });
                    handleCardExpand(key + '', node, true);
                }).catch(() => {
                    setNodeLoadingStateMap(map => {
                        return Object.assign(Object.assign({}, map), { [key + '']: false });
                    });
                });
            }
            else {
                console.warn('[VlistTree]: loadData 方法必须返回Promise');
            }
        }
    }, [loadData, handleCardExpand]);
    const overPageCheck = useCallback((st) => {
        let index = getListIndexByStBottom(st, height);
        const curNodeKey = showKeyList[index];
        const nodeConfig = key2ConfigMap[curNodeKey];
        const parentNode = nodeConfig === null || nodeConfig === void 0 ? void 0 : nodeConfig.parent;
        if (listIsLoadingRef.current) {
            return false;
        }
        if (parentNode) {
            if (!(parentNode === null || parentNode === void 0 ? void 0 : parentNode.hasMore)) {
                return false;
            }
        }
        else if (!hasMore) {
            return false;
        }
        if (parentNode) {
            const children = parentNode.children;
            if (children && children.length) {
                const aimNodeKey = getUniqKeyRef(children[children.length - 1]);
                setSplitKey(aimNodeKey);
            }
        }
        else {
            setSplitKey(null);
        }
    }, [showKeyList, key2ConfigMap, hasMore]);
    const throttleOverPageCheck = useThrottleForEvent(overPageCheck, 500);
    const debounceSetSt = useDebounceForEvent(({ scrollTop }) => _setScrollTop(scrollTop), 500);
    const handleScroll = useCallback(({ scrollTop: st }) => {
        throttleOverPageCheck(st);
        onScroll && onScroll(st);
        debounceSetSt(st);
    }, [onScroll]);
    const handleExpand = (expand, node) => {
        var _a;
        if (asyncLoad && !((_a = node.children) === null || _a === void 0 ? void 0 : _a.length) && expand) {
            handleCardLoadChildren(node);
        }
        else {
            handleCardExpand(getUniqKeyRef(node), node, expand);
        }
    };
    const getVlistCard = useCallback(cardProps => {
        var _a;
        const { data: nodeKey } = cardProps;
        const node = key2NodeMap[nodeKey];
        const isLoading = !!nodeLoadingStateMap[nodeKey];
        const isLeaf = node === null || node === void 0 ? void 0 : node.isLeaf;
        const children = node === null || node === void 0 ? void 0 : node.children;
        let showExpand = !!(children === null || children === void 0 ? void 0 : children.length);
        if (asyncLoad) {
            showExpand = !isLeaf;
        }
        return (React.createElement(VListTreeCard, { data: node, icon: icon, showExpand: showExpand, deep: (_a = key2ConfigMap[nodeKey]) === null || _a === void 0 ? void 0 : _a.deep, expand: !!curNodeKeyExpandMap[nodeKey], isLoading: isLoading, onExpand: expand => {
                handleExpand(expand, node);
            }, onRightClick: handleCardRightClick, onClick: (event, data) => {
                handleCardClick(nodeKey, event, data);
            }, active: !!selectKeyMap[nodeKey], titleRender: titleRender, calssName: cardClassName }));
    }, [key2NodeMap, nodeLoadingStateMap, asyncLoad, key2ConfigMap, curNodeKeyExpandMap, icon, selectKeyMap, cardClassName]);
    const listHasMore = useMemo(() => {
        const st = _stRef.current || 0;
        let index = getListIndexByStBottom(st, height);
        const curNodeKey = showKeyList[index];
        const nodeConfig = key2ConfigMap[curNodeKey];
        const parentNode = nodeConfig === null || nodeConfig === void 0 ? void 0 : nodeConfig.parent;
        if (parentNode) {
            return !!(parentNode === null || parentNode === void 0 ? void 0 : parentNode.hasMore) ? (showKeyList === null || showKeyList === void 0 ? void 0 : showKeyList.length) + 1 : 0;
        }
        else {
            return hasMore ? (showKeyList === null || showKeyList === void 0 ? void 0 : showKeyList.length) + 1 : 0;
        }
    }, [hasMore, showKeyList, key2ConfigMap]);
    const getParentNodeByStLine = useCallback(() => {
        try {
            const st = _stRef.current || 0;
            if (st != null) {
                let index = getListIndexByStBottom(st, height);
                const curNodeKey = showKeyList[index];
                const nodeConfig = key2ConfigMap[curNodeKey];
                const parentNode = nodeConfig === null || nodeConfig === void 0 ? void 0 : nodeConfig.parent;
                return parentNode;
            }
        }
        catch (e) {
            console.error('[Error:getParentNodeByStLine]', e);
            return null;
        }
    }, [showKeyList]);
    const handleListLoadMore = useCallback((start) => {
        var _a;
        setListIsLoading(true);
        listIsLoadingRef.current = true;
        if (onLoadMoreNode) {
            const pNode = getParentNodeByStLine() || null;
            return (_a = onLoadMoreNode(start, pNode)) === null || _a === void 0 ? void 0 : _a.finally(() => {
                setSplitKey(null);
                setListIsLoading(false);
                listIsLoadingRef.current = false;
            });
        }
        return Promise.resolve();
    }, [onLoadMoreNode, getParentNodeByStLine]);
    const getVList = useCallback((_width, _height) => {
        let logicHeight = _height;
        if (realSize && resKeyList) {
            const length = (resKeyList === null || resKeyList === void 0 ? void 0 : resKeyList.length) || 0;
            const realHeight = length * getCardHeight();
            if (realHeight < _height) {
                logicHeight = realHeight + 10;
            }
        }
        return (React.createElement(CardList, { className: rootClassName, style: rootStyle, width: _width, height: logicHeight, initLoadMore: false, onLoadMore: handleListLoadMore, rowHeight: getCardHeight, data: resKeyList, card: getVlistCard, getUniqKey: (index, key) => key, total: listHasMore, scrollTop: mgScrollTop, onScroll: handleScroll, cardMargin: TreeCardMargin, cardGroupDecorate: cardGroupDecorate, noMoreRender: () => React.createElement(React.Fragment, null) }));
    }, [rootClassName, realSize, rootStyle, handleListLoadMore, getCardHeight, resKeyList, getVlistCard, listHasMore, handleScroll, mgScrollTop, cardGroupDecorate]);
    useEffect(() => {
        if (treeData == null || treeData.length == 0) {
            _setScrollTop(0);
        }
        if (nodeLoadingStateMap) {
            const key2NodeMap = {};
            for (let i in nodeLoadingStateMap) {
                if (!key2NodeMap[i + '']) {
                    nodeLoadingStateMap[i + ''] = false;
                }
            }
            setNodeLoadingStateMap(Object.assign({}, nodeLoadingStateMap));
        }
    }, [treeData]);
    useEffect(() => {
        if (expandedKeys && _expandedKeys && _expandedKeys.length) {
            _setExpandedKeys([]);
        }
    }, [expandedKeys]);
    return autoSize ? (React.createElement("div", { style: { width: '100%', height: '100%' } },
        React.createElement(AutoSizer, null, ({ height, width }) => getVList(width, height)))) : (useMemo(() => {
        return getVList(width, height);
    }, [width, height, getVList]));
};
export default VListTree;
